/* ============================================================
 * Lion Theme — lion-predictive-search.js
 * <lion-search-modal>   → overlay + focus management
 * <lion-predictive-search> → typeahead results pane
 * ============================================================ */

class SearchModal extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.form = this.querySelector('[data-predictive-search-form]');
    this.resetBtn = this.querySelector('[data-predictive-search-reset]');

    this.querySelectorAll('[data-search-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.addEventListener('keyup', (e) => {
      if (e.code?.toUpperCase() === 'ESCAPE') this.close();
    });

    document.querySelectorAll('[data-search-toggle]').forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(el);
      })
    );

    this.resetBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.resetBtn.hidden = true;
      this.results?.reset();
      this.input.focus();
    });

    this.input?.addEventListener('input', () => {
      if (this.resetBtn) this.resetBtn.hidden = !this.input.value;
    });
  }

  get results() {
    return this.querySelector('lion-predictive-search');
  }

  open(trigger) {
    this.openedBy = trigger || document.activeElement;
    this.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      window.trapFocus?.(this, this.input);
    });
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    window.removeTrapFocus?.(this.openedBy);
    setTimeout(() => this.setAttribute('hidden', ''), 240);
  }
}
customElements.define('lion-search-modal', SearchModal);

/* ------------------------------------------------------------
 * <lion-predictive-search>
 * ------------------------------------------------------------ */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.modal = this.closest('lion-search-modal');
    this.input = this.modal?.querySelector('[data-predictive-search-input]');
    this.liveRegion = this.querySelector('[data-predictive-search-live]');
    this.default = this.querySelector('.predictive-search__default');
    this.cache = new Map();
    this.abortController = null;

    if (!this.input) return;

    this.input.addEventListener('input', window.debounce?.(this.onInput.bind(this), 180) || this.onInput.bind(this));
    this.input.addEventListener('focus', this.onInput.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  reset() {
    if (this.liveRegion) { this.liveRegion.innerHTML = ''; this.liveRegion.hidden = true; }
    if (this.default) this.default.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
  }

  async onInput() {
    const term = this.input.value.trim();
    if (term.length < 2) { this.reset(); return; }

    // Cache hit
    if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const fields = ['title', 'product_type', 'variants.title', 'vendor'];
      // Predictive Search API field token is the singular 'tag' ('tags' is the
      // response property name, not a valid searchable field — sending it makes
      // Shopify ignore/reject the param, silently breaking tag search).
      if (this.dataset.searchByTag === 'true') fields.push('tag');
      if (this.dataset.searchByBody === 'true') fields.push('body');
      const unavailable = this.dataset.unavailableProducts || 'last';
      const url = `${window.shopUrls.predictive_search}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[limit]=6&resources[limit_scope]=each&resources[options][fields]=${fields.join(',')}&resources[options][unavailable_products]=${unavailable}`;
      const res = await fetch(url, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const results = doc.querySelector('.predictive-search__results');
      const markup = results ? results.outerHTML : doc.body.innerHTML;
      this.cache.set(term, markup);
      this.render(markup);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[lion-predictive-search]', err);
    }
  }

  render(html) {
    if (!this.liveRegion) return;
    this.liveRegion.innerHTML = html;
    this.liveRegion.hidden = false;
    if (this.default) this.default.hidden = true;
    this.input.setAttribute('aria-expanded', 'true');
  }

  onKeydown(event) {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const items = [...this.querySelectorAll('a[href]')];
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement);
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      items[(idx + 1) % items.length].focus();
    } else {
      items[(idx - 1 + items.length) % items.length].focus();
    }
  }
}
customElements.define('lion-predictive-search', PredictiveSearch);
